import Phaser from "phaser";
import type { BootConfiguration } from "../core/Boot";
import { CAMERA_RUNTIME } from "../core/CameraRuntime";
import { CodeRainRenderer } from "../view/CodeRainRenderer";
import { LayoutBitmapTextRenderer } from "../view/LayoutBitmapTextRenderer";
import { SYMBOL_SEQUENCE_FONT_CONFIGS } from "../view/SymbolSequenceFont";
import { LevelScene } from "./LevelScene";
import { LevelSelectScene } from "./LevelSelectScene";
import {
  DEFAULT_BGM_VOLUME,
  HOME_BGM_KEY,
  LEVEL0_UNPATTERNED_IMAGE_KEY,
} from "./BootScene";

export interface HomeSceneBootData {
  readonly bootConfiguration?: BootConfiguration;
}

export type { HomeSceneBootData as HomeSceneInitData };

export interface HomeRouteTarget {
  readonly scene: string;
  readonly data?: Readonly<Record<string, unknown>>;
}

export function determineHomeRoute(config: BootConfiguration | null): HomeRouteTarget {
  if (config !== null && config.hasCrossedOnboardingBoundary) {
    return { scene: LevelSelectScene.KEY };
  }
  return {
    scene: LevelScene.KEY,
    data: {
      levelId: "level-0-1",
      transitionIn: { color: "black", durationMs: 500 },
    },
  };
}

export class HomeScene extends Phaser.Scene {
  public static readonly KEY = "HomeScene";

  private static readonly PROMPT_ALPHA_STEPS = [0, 0.25, 0.5, 0.75, 1, 0.75, 0.5, 0.25, 0] as const;

  private bootConfiguration: BootConfiguration | null = null;
  private codeRainRenderer: CodeRainRenderer | null = null;
  private promptText: LayoutBitmapTextRenderer | null = null;
  private titleImage: Phaser.GameObjects.Image | null = null;
  private homeBgm: Phaser.Sound.BaseSound | null = null;
  private keyboardListener: ((event: KeyboardEvent) => void) | null = null;
  private promptBlinkElapsed: number = 0;

  public constructor() {
    super(HomeScene.KEY);
  }

  public init(data: HomeSceneBootData = {}): void {
    this.bootConfiguration = data.bootConfiguration ?? null;
    this.destroyCodeRain();
    this.destroyPromptText();
    this.destroyTitleImage();
    this.destroyHomeBgm();
    this.promptBlinkElapsed = 0;
  }

  public create(): void {
    this.cameras.main.setBackgroundColor("#000000");
    this.codeRainRenderer = new CodeRainRenderer(
      this,
      CAMERA_RUNTIME.viewportWidthPx,
      CAMERA_RUNTIME.viewportHeightPx,
    );

    const { width, height } = this.cameras.main;
    this.titleImage = this.add.image(width / 2, height / 2 - 40, LEVEL0_UNPATTERNED_IMAGE_KEY);
    this.titleImage.setOrigin(0.5, 0.5);
    this.titleImage.setScrollFactor(0);

    this.promptText = new LayoutBitmapTextRenderer(this, {
      depth: 1,
      tint: 0xffffff,
      fontProfile: SYMBOL_SEQUENCE_FONT_CONFIGS["12px"],
    });
    this.promptText.render("PRESS ANY KEY TO START", {
      centerX: width / 2,
      y: height - 16,
      verticalAlign: "middle",
    });
    this.promptText.setAlpha(0);

    this.playHomeBgm();
    this.publishSceneName();
    this.setupInput();
    this.bindCleanup();
  }

  public override update(_time: number, delta: number): void {
    if (delta <= 0) {
      return;
    }

    this.codeRainRenderer?.update(delta);
    this.codeRainRenderer?.render();

    this.promptBlinkElapsed += delta;
    const stepIndex = Math.floor(this.promptBlinkElapsed / 200) % HomeScene.PROMPT_ALPHA_STEPS.length;
    const alpha = HomeScene.PROMPT_ALPHA_STEPS[stepIndex] ?? 0;
    this.promptText?.setAlpha(alpha);
  }

  private publishSceneName(): void {
    // Debug scene-name display removed — see AGENTS.md TODO #9.
  }

  private setupInput(): void {
    const keyboard = this.input.keyboard;
    if (keyboard === undefined || keyboard === null) {
      return;
    }

    this.keyboardListener = (_event: KeyboardEvent) => {
      this.handleStart();
    };

    keyboard.on("keydown", this.keyboardListener);
  }

  private handleStart(): void {
    this.removeKeyboardListener();
    const target = determineHomeRoute(this.bootConfiguration);
    this.scene.start(target.scene, target.data);
  }

  private bindCleanup(): void {
    this.events.once("shutdown", () => {
      this.destroyHomeBgm();
      this.destroyCodeRain();
      this.destroyPromptText();
      this.destroyTitleImage();
      this.removeKeyboardListener();
    });
    this.events.once("destroy", () => {
      this.destroyHomeBgm();
      this.destroyCodeRain();
      this.destroyPromptText();
      this.destroyTitleImage();
      this.removeKeyboardListener();
    });
  }

  private destroyCodeRain(): void {
    this.codeRainRenderer?.destroy();
    this.codeRainRenderer = null;
  }

  private destroyPromptText(): void {
    if (this.promptText !== null) {
      this.promptText.destroy();
      this.promptText = null;
    }
  }

  private destroyTitleImage(): void {
    if (this.titleImage !== null) {
      this.titleImage.destroy();
      this.titleImage = null;
    }
  }

  private playHomeBgm(): void {
    const existingSound = this.sound.get(HOME_BGM_KEY);
    if (existingSound !== null) {
      existingSound.stop();
      existingSound.destroy();
    }

    this.homeBgm = this.sound.add(HOME_BGM_KEY, {
      loop: true,
      volume: DEFAULT_BGM_VOLUME,
    });
    this.homeBgm.play();
  }

  private destroyHomeBgm(): void {
    if (this.homeBgm !== null) {
      this.homeBgm.stop();
      this.homeBgm.destroy();
      this.homeBgm = null;
      return;
    }

    const existingSound = this.sound.get(HOME_BGM_KEY);
    if (existingSound !== null) {
      existingSound.stop();
      existingSound.destroy();
    }
  }

  private removeKeyboardListener(): void {
    const keyboard = this.input.keyboard;
    if (keyboard !== undefined && keyboard !== null && this.keyboardListener !== null) {
      keyboard.off("keydown", this.keyboardListener);
    }
    this.keyboardListener = null;
  }

  public getBootConfiguration(): BootConfiguration | null {
    return this.bootConfiguration;
  }
}
